"use client";

import { AUTH_TOKEN } from "constants/AuthConstant";
import { useState,useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setPaiementsState } from "store/slices/paiements";
import { isAuth } from "./isAuth";
import { setAuthUser, signOutSuccess } from "store/slices/authSlice";
import { refreshAccessToken, isRefreshingToken } from "refreshToken";
import dayjs from "dayjs";

const usePaiementEmployee = () => {
  const [paiements, setPaiements] = useState(null);
  const [paiement, setPaiement] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const base_url = process.env.REACT_APP_API_URL;

  const url_getAllPaiements = base_url + "/payments";
  const url_search = base_url + "/payments/search-by-date?date=";

  const dispatch = useDispatch();
  const tokenFromState = useSelector((state) => state.auth.token);

  // if (localStorage.getItem(AUTH_TOKEN) !== tokenFromState) {
  //   localStorage.removeItem(AUTH_TOKEN)
  //   dispatch(signOutSuccess())
  //   dispatch(setAuthUser({}))
  // }

    // Vérification token
  useEffect(() => {
    if (localStorage.getItem(AUTH_TOKEN) !== tokenFromState) {
      localStorage.removeItem(AUTH_TOKEN);
      dispatch(signOutSuccess());
      dispatch(setAuthUser({}));
    }
  }, [dispatch, tokenFromState]);

  // Fonction générique pour les appels API avec gestion du token
 const authFetch = useCallback(async (url, options = {}, navigate) => {
    let token = tokenFromState || localStorage.getItem(AUTH_TOKEN);
    
    if (!token && !isRefreshingToken()) {
      token = await refreshAccessToken();
    }

    if (!token) throw new Error("Authentification requise");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    };

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        body: options.body,
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (!newToken) {
          localStorage.removeItem(AUTH_TOKEN);
          dispatch(signOutSuccess());
          dispatch(setAuthUser({}));
          if (navigate) navigate("/login");
          throw new Error("Session expirée");
        }
        return fetch(url, {
          ...options,
          headers: { ...headers, Authorization: `Bearer ${newToken}` },
        });
      }

      return response;
    } catch (error) {
      console.error("Erreur API:", error);
      throw error;
    }
  }, [dispatch, tokenFromState]);


  // Gestion centralisée des erreurs API
  const handleApiError = (errorData) => {
    if (!isAuth(errorData)) {
      localStorage.removeItem(AUTH_TOKEN)
      dispatch(signOutSuccess())
      dispatch(setAuthUser({}))
    }

    if (errorData?.statusCode === 500) {
      setError("Erreur interne du serveur")
      return
    }

    if (Array.isArray(errorData?.data?.message)) {
      setError(errorData.data.message[0])
      return
    }

    if (errorData?.data?.message) {
      setError(errorData.data.message)
      return
    }

    if (errorData?.message) {
      setError(errorData.message)
      return
    }

    setError("Erreur inconnue")
  }

  const getAllPaiements = async (navigate, limit = 10, page = 1) => {
    setIsLoading(true)
    setError(null)

    try {
      const url = new URL(url_getAllPaiements)
      url.searchParams.append("limit", limit)
      url.searchParams.append("page", page)

      const response = await authFetch(url.toString(), { method: "GET" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const data = await response.json()
      setPaiements(data.data)
      dispatch(setPaiementsState(data.data))
    } catch (err) {
      console.error("Échec de récupération des paiements :", err)
      setError(err.message || "Échec de récupération des paiements")
    } finally {
      setIsLoading(false)
    }
  }

  const deletePaiement = async (navigate, id) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${url_getAllPaiements}/${id}`, { method: "DELETE" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      setPaiements([])
      await getAllPaiements(navigate)
    } catch (err) {
      console.error("Échec de suppression :", err)
      setError(err.message || "Échec de suppression")
    } finally {
      setIsLoading(false)
    }
  }

  const addPaiement = async (navigate, data, id) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(
        `${url_getAllPaiements}/${id}`,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        navigate,
      )

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const responseData = await response.json()
      setPaiements(responseData)
      await getAllPaiements(navigate)
    } catch (err) {
      console.error("Échec d'ajout :", err)
      setError(err.message || "Échec d'ajout")
    } finally {
      setIsLoading(false)
    }
  }

  const getPaiementsByDate = async (navigate, dateSearch) => {
    if (!dateSearch) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${url_search}${dateSearch}`, { method: "GET" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const data = await response.json()
      setPaiements(data)
      dispatch(setPaiementsState(data))
    } catch (err) {
      console.error("Échec de recherche :", err)
      setError(err.message || "Échec de recherche")
    } finally {
      setIsLoading(false)
    }
  }

const updatePaiement = async (navigate, data, id) => {
    setIsLoading(true);
    setError(null);

    try {
        // Validation des données
      if (!data.amount || !data.paymentDate) {
            throw new Error("Données manquantes");
        }

        const response = await authFetch(
            `${url_getAllPaiements}/${id}`,
            {
                method: "PUT",
                body: JSON.stringify({
                    amount: data.amount,
                    paymentDate: data.paymentDate // Retirer employeeId
                }),
            },
            navigate
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || "Erreur serveur");
        }

        // Force le rafraîchissement des données
        await getAllPaiements(navigate);
        return true;
    } catch (err) {
        setError(err.message);
        return false;
    } finally {
        setIsLoading(false);
    }
};

  const getPaiementById = async (navigate, id) => {
    if (!id) return null

    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${url_getAllPaiements}/${id}`, { method: "GET" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return null
      }

      const data = await response.json()
      setPaiement(data)
      return data
    } catch (err) {
      console.error("Échec de récupération :", err)
      setError(err.message || "Échec de récupération")
      return null
    } finally {
      setIsLoading(false)
    }
  }

  return {
    getPaiementById,
    updatePaiement,
    getPaiementsByDate,
    addPaiement,
    deletePaiement,
    getAllPaiements,
    paiement,
    setPaiement,
    paiements,
    setPaiements,
    isLoading,
    error,
    setError,
  }
}

export default usePaiementEmployee